import { Record } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { isDemoMode } from 'app-components/utils/core';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { CoreAction } from '../core/resourceStoreTypes';
import * as actions from './preferencesV2Actions';
import { getLastSyncDate, getPreferencesV2 } from './preferencesV2Selectors';
import {
  mkPreferenceV2,
  type PreferenceOverrideBumpResource,
  type PreferenceV2,
  type PreferenceV2Props,
} from './preferencesV2Types';
import { isValidJSON, normalizeValue, transformResourceValue } from './preferencesV2Utils';

type PreferencesConfigProps = resourceStoreTypes.QcsSyncResourcesConfigProps<PreferenceV2Props>;

const mkQcsSyncPreferencesV2Config = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<PreferenceV2Props>();
type PreferenceConfig = ReturnType<typeof mkQcsSyncPreferencesV2Config>;

const mkResourceConfig = (props: Partial<PreferencesConfigProps>) =>
  mkQcsSyncPreferencesV2Config({
    resourceName: 'preferences-v2',
    resourceBaseUrl: '/v2/preferences',
    getLastSyncDate,
    ...props,
  });

export function* getPreferencesV2ActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences',
    successAction: actions.getPreferencesV2Success,
    failureAction: actions.getPreferencesV2Failure,
    // axiosConfig: { headers: { 'qcs-dataset-id': null } },
    // bc pref-v2 names are unique, we want to key off names for fast lookups
    transformResponseToResources: (response) =>
      response?.data?.resources?.map((resource) => {
        const respObj = {
          ...resource,
          id: resource?.name,
          preferenceId: resource?.id,
          value: normalizeValue(resource?.dataType, resource?.value),
        };
        return Record(respObj)(respObj);
      }),
  });
  yield takeEvery(actions.getPreferencesV2.type, resourceSagaUtils.qcsSyncGetResources<PreferenceV2Props>, config);
}

export function* getPreferenceV2ActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences',
    successAction: actions.getSpecificPreferenceV2Success,
    failureAction: actions.getSpecificPreferenceV2Failure,
    transformResponseToResource: ({ data }) =>
      mkPreferenceV2({
        ...data,
        id: data?.name,
        preferenceId: data?.id,
        value: normalizeValue(data?.dataType, data?.value),
      }),
  });
  yield takeEvery(
    actions.getSpecificPreferenceV2.type,
    resourceSagaUtils.qcsSyncGetResource<PreferenceV2Props>,
    config,
  );
}

function* updatePreferenceV2Saga(configuration: PreferenceConfig, action: CoreAction<any, any>) {
  const dataType = action?.payload?.dataType;
  let isValid = true;
  if (dataType && (dataType === 'JSON' || dataType === 'ARRAY')) {
    isValid = assert(isValidJSON(action?.payload?.value), 'invalid JSON pref value', action);
  }
  if (isValid) {
    yield call(resourceSagaUtils.qcsSyncCreateResource<PreferenceV2Props>, configuration, action);
  }
}

export const toPreferenceOverrideRequestData = (resource: PreferenceV2Props | PreferenceV2) => ({
  ...('toJS' in resource ? (resource.toJS() as PreferenceV2Props) : resource),
  value: transformResourceValue(Record.isRecord(resource) ? resource : mkPreferenceV2(resource)),
  preferenceName: resource.preferenceName ?? resource.id,
  id: undefined,
});

export function* updatePreferenceOverrideActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: () => '/v2/preferences/override',
    successAction: actions.updatePreferenceOverrideSuccess,
    failureAction: actions.updatePreferenceOverrideFailure,
    // adding id here results in a 400 error due to override having a separate id not accessible to the client
    transformResourceToRequestData: toPreferenceOverrideRequestData,
  });
  if (!isDemoMode()) {
    yield takeEvery(actions.updatePreferenceOverride.type, updatePreferenceV2Saga, config);
  }
}

export function* resetPreferenceOverrideActionWatcher() {
  yield takeEvery(actions.resetPreferenceOverride, function* resetPreferenceOverride(action) {
    yield call(resourceSagaUtils.qcsRequest, {
      method: 'POST',
      path: 'v2/preferences/reset-preference',
      data: { preferenceName: action.payload.id },
    });
    yield put(actions.getSpecificPreferenceV2({ id: action.payload.id }));
  });
}

export function* preferencesNudgeUpdater() {
  yield put(actions.getPreferencesV2(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
}

export function* preferencesDumpUpdater(resources: Array<PreferenceOverrideBumpResource>): SagaIterator {
  const prefs = yield select(getPreferencesV2);
  yield put(
    actions.applyPreferenceChanges({
      doMerge: false,
      // TODO: remove filter once QCS deliver https://quickenteam.atlassian.net/browse/QCS-19834
      resources: resources
        .filter((resource) => prefs.get(resource.preferenceName)?.dataType)
        .map((resource) => {
          const pref = prefs.get(resource.preferenceName);
          return mkPreferenceV2({
            ...(pref?.toJS?.() || pref),
            ...resource,
            id: resource.preferenceName,
            name: resource.preferenceName,
            value: normalizeValue(pref.dataType, resource.value), // TODO: use resouce.dataType filter once QCS deliver https://quickenteam.atlassian.net/browse/QCS-19834
          });
        }),
    }),
  );
}

export default [
  getPreferencesV2ActionWatcher,
  getPreferenceV2ActionWatcher,
  resetPreferenceOverrideActionWatcher,
  updatePreferenceOverrideActionWatcher,
];
